import React, { useState, FormEvent, useCallback, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getErrorMessage, getClosedBetaRegistrationDetails, isClosedBetaRegistrationError } from '../types';
import { useTranslation } from '../i18n';
import { useApiRequest } from '../useApiRequest';
import { ClosedBeta } from './ClosedBeta';
import { FormLabel } from './FormLabel';
import { validateFormSubmit } from '../utils/formValidation';
import { normalizePhoneNumber } from '../utils/phoneNumber';
import { PhoneInput } from './PhoneInput';
import { TermsAcceptance, MarketingConsentCheckbox } from './TermsAcceptance';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
}

interface SignupSessionResponse {
  requiresEmail: boolean;
  phoneNumber?: string | null;
}

interface ExternalSignupProps {
  onLogin: (accessToken: string, refreshToken: string) => void;
}

export const ExternalSignup: React.FC<ExternalSignupProps> = ({ onLogin }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const provider = searchParams.get('provider');

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [requiresEmail, setRequiresEmail] = useState(searchParams.get('requires_email') === '1');
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'error' | 'success' | null>(null);
  const [registrationClosed, setRegistrationClosed] = useState(false);
  const [closedBetaDetails, setClosedBetaDetails] = useState({ email: '', firstName: '', lastName: '' });

  useEffect(() => {
    if (!code || !provider) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get<SignupSessionResponse>(
          `${API_URL}/auth/external/${provider}/signup-session`,
          { params: { code } }
        );
        if (cancelled) return;
        setRequiresEmail(res.data.requiresEmail);
        if (res.data.phoneNumber) {
          setPhoneNumber(res.data.phoneNumber);
        }
        setSessionLoaded(true);
      } catch (err) {
        if (cancelled) return;
        setSessionError(getErrorMessage(err, t('social.error_generic')));
        setSessionLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, provider, t]);

  const { loading, call } = useApiRequest(async () => {
    const res = await axios.post<AuthResponse>(`${API_URL}/auth/external/${provider}/signup`, {
      code,
      username,
      email: requiresEmail ? email.trim() : undefined,
      phoneNumber: normalizePhoneNumber(phoneNumber),
      acceptTerms,
      marketingConsent,
    });
    onLogin(res.data.accessToken, res.data.refreshToken);
    navigate('/', { replace: true });
  });

  const handleSubmit = useCallback(async (e: FormEvent) => {
    if (!validateFormSubmit(e)) return;
    e.preventDefault();
    setMessage('');
    setMessageType(null);
    setRegistrationClosed(false);
    setClosedBetaDetails({ email: '', firstName: '', lastName: '' });
    try {
      await call();
    } catch (err) {
      if (isClosedBetaRegistrationError(err)) {
        const details = getClosedBetaRegistrationDetails(err);
        setClosedBetaDetails({
          email: details.email ?? '',
          firstName: details.firstName ?? '',
          lastName: details.lastName ?? '',
        });
        setRegistrationClosed(true);
        return;
      }
      setMessage(getErrorMessage(err, t('social.error_generic')));
      setMessageType('error');
    }
  }, [call, t]);

  if (!code || !provider) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>{t('social.complete_title')}</h2>
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {t('social.error_missing_code')}
          </div>
          <Link to="/login" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
            {t('social.back_to_login')}
          </Link>
        </div>
      </div>
    );
  }

  if (!sessionLoaded) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>{t('social.complete_title')}</h2>
          <p className="login-subtitle">{t('social.complete_subtitle')}</p>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>{t('social.complete_title')}</h2>
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {sessionError}
          </div>
          <Link to="/login" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
            {t('social.back_to_login')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        {registrationClosed ? (
          <>
            <ClosedBeta
              inline
              email={closedBetaDetails.email}
              firstName={closedBetaDetails.firstName}
              lastName={closedBetaDetails.lastName}
            />
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Link to="/login" className="forgot-link">{t('social.back_to_login')}</Link>
            </div>
          </>
        ) : (
          <>
            <h2>{t('social.complete_title')}</h2>
            <p className="login-subtitle">{t('social.complete_subtitle')}</p>

            {message && (
              <div
                className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`}
                style={{ marginBottom: '1rem' }}
              >
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <FormLabel required>{t('social.username_label')}</FormLabel>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder={t('social.username_placeholder')}
                  required
                  disabled={loading}
                  aria-required="true"
                />
              </div>
              {requiresEmail && (
                <div className="form-group">
                  <FormLabel required>{t('social.email_label')}</FormLabel>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="email"
                    aria-required="true"
                  />
                </div>
              )}
              <div className="form-group">
                <FormLabel required>{t('social.phone_label')}</FormLabel>
                <PhoneInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  placeholder={t('login.phone_placeholder')}
                  required
                  disabled={loading}
                  aria-required="true"
                  invalidMessage={t('validation.phone_invalid')}
                />
              </div>
              <TermsAcceptance checked={acceptTerms} onChange={setAcceptTerms} disabled={loading} />
              <MarketingConsentCheckbox checked={marketingConsent} onChange={setMarketingConsent} disabled={loading} />
              <div className="form-actions" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                  {loading && <span className="btn-spinner" aria-hidden="true" />}
                  {loading ? t('social.submitting') : t('social.submit')}
                </button>
                <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                  <Link to="/login" className="forgot-link">{t('social.back_to_login')}</Link>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
